/**
 * Types partagés pour le plateau de jeu (tour de chauffe avant chaque manche
 * de qualification) — voir le plan Phase 1 pour le contexte complet.
 */

/** Type de case du plateau, tiré aléatoirement une fois par meeting. */
export type BoardCellType =
  | 'upgradeMeeting'
  | 'malusMeeting'
  | 'upgradeCarPermanent'
  | 'upgradeDriverPermanent'
  | 'jokerSeasonUpgrade'
  | 'jokerMeetingUpgrade'
  | 'randomCard'
  | 'carPurchase'
  | 'casino'
  | 'bonusPoints'
  | 'malusPoints';

export const BOARD_CELL_TYPES: readonly BoardCellType[] = [
  'upgradeMeeting',
  'malusMeeting',
  'upgradeCarPermanent',
  'upgradeDriverPermanent',
  'jokerSeasonUpgrade',
  'jokerMeetingUpgrade',
  'randomCard',
  'carPurchase',
  'casino',
  'bonusPoints',
  'malusPoints',
];

/** Cible tirée pour une amélioration/malus de meeting : les 4 stats + le bonus Joker. */
export type MeetingStatTarget = 'pilotage' | 'defense' | 'engine' | 'tire' | 'jokerLap';

export const MEETING_STAT_TARGETS: readonly MeetingStatTarget[] = [
  'pilotage',
  'defense',
  'engine',
  'tire',
  'jokerLap',
];

/** Cible d'une amélioration permanente voiture (jamais le bonus Joker). */
export type CarPermanentStatTarget = 'engine' | 'tire';
export const CAR_PERMANENT_STAT_TARGETS: readonly CarPermanentStatTarget[] = ['engine', 'tire'];

/** Cible d'une amélioration permanente pilote (jamais le bonus Joker). */
export type DriverPermanentStatTarget = 'pilotage' | 'defense';
export const DRIVER_PERMANENT_STAT_TARGETS: readonly DriverPermanentStatTarget[] = [
  'pilotage',
  'defense',
];

/** Poids (%) de tirage par type de case — doit sommer à 100. */
export type BoardCellWeights = Record<BoardCellType, number>;

export interface BoardSettings {
  weights: BoardCellWeights;
  /** Coût pour valider une amélioration de meeting. */
  costUpgradeMeeting: number;
  /** Coût pour annuler un malus de meeting. */
  costMalusAvoid: number;
  /** Coût d'une amélioration permanente voiture. */
  costCarPermanent: number;
  /** Coût d'une amélioration permanente pilote. */
  costDriverPermanent: number;
  /** Coût du bonus Joker saisonnier. */
  costJokerSeason: number;
  /** Coût du bonus Joker de meeting. */
  costJokerMeeting: number;
  /** Coût d'un achat de voiture (au moins aussi bonne que la sienne). */
  costCarPurchase: number;
  /** Plage du bonus de points (case bonusPoints). */
  bonusPointsMin: number;
  bonusPointsMax: number;
  /** Plage du malus de points (case malusPoints). */
  malusPointsMin: number;
  malusPointsMax: number;
  /** Multiplicateur de gain au casino (mise × multiplicateur si le numéro tombe). */
  casinoMultiplier: number;
}

export const DEFAULT_BOARD_SETTINGS: BoardSettings = {
  weights: {
    upgradeMeeting: 25,
    malusMeeting: 25,
    upgradeCarPermanent: 5,
    upgradeDriverPermanent: 5,
    jokerSeasonUpgrade: 5,
    jokerMeetingUpgrade: 5,
    randomCard: 5,
    carPurchase: 5,
    casino: 10,
    bonusPoints: 5,
    malusPoints: 5,
  },
  costUpgradeMeeting: 8,
  costMalusAvoid: 5,
  costCarPermanent: 30,
  costDriverPermanent: 50,
  costJokerSeason: 20,
  costJokerMeeting: 7,
  costCarPurchase: 50,
  bonusPointsMin: 1,
  bonusPointsMax: 10,
  malusPointsMin: 1,
  malusPointsMax: 10,
  casinoMultiplier: 5,
};

function clampNonNegativeInt(v: unknown, fallback: number): number {
  const n = Number(v);
  if (!Number.isFinite(n)) return fallback;
  return Math.max(0, Math.round(n));
}

/** Somme des poids (%) — pour l'affichage live côté écran de réglages admin. */
export function sumBoardWeights(weights: Partial<Record<BoardCellType, unknown>>): number {
  let sum = 0;
  for (const key of BOARD_CELL_TYPES) {
    const v = Number(weights[key]);
    if (Number.isFinite(v)) sum += v;
  }
  return sum;
}

function isWeightsSumTo100(weights: unknown): weights is BoardCellWeights {
  if (!weights || typeof weights !== 'object') return false;
  const w = weights as Record<string, unknown>;
  for (const key of BOARD_CELL_TYPES) {
    const v = Number(w[key]);
    if (!Number.isFinite(v) || v < 0) return false;
  }
  return sumBoardWeights(w) === 100;
}

/**
 * Valide/clampe des réglages de plateau bruts (ex. body d'API admin).
 * Si la forme est invalide (poids absents ou ne sommant pas à 100), retombe
 * intégralement sur `DEFAULT_BOARD_SETTINGS` — pas de fusion partielle qui
 * laisserait passer une somme de poids incohérente.
 */
export function normalizeBoardSettings(raw: unknown): BoardSettings {
  if (!raw || typeof raw !== 'object') return { ...DEFAULT_BOARD_SETTINGS };
  const r = raw as Record<string, unknown>;

  const weights = isWeightsSumTo100(r.weights)
    ? { ...r.weights }
    : { ...DEFAULT_BOARD_SETTINGS.weights };

  const bonusPointsMin = clampNonNegativeInt(
    r.bonusPointsMin,
    DEFAULT_BOARD_SETTINGS.bonusPointsMin
  );
  const bonusPointsMaxRaw = clampNonNegativeInt(
    r.bonusPointsMax,
    DEFAULT_BOARD_SETTINGS.bonusPointsMax
  );
  const malusPointsMin = clampNonNegativeInt(
    r.malusPointsMin,
    DEFAULT_BOARD_SETTINGS.malusPointsMin
  );
  const malusPointsMaxRaw = clampNonNegativeInt(
    r.malusPointsMax,
    DEFAULT_BOARD_SETTINGS.malusPointsMax
  );

  return {
    weights,
    costUpgradeMeeting: clampNonNegativeInt(
      r.costUpgradeMeeting,
      DEFAULT_BOARD_SETTINGS.costUpgradeMeeting
    ),
    costMalusAvoid: clampNonNegativeInt(r.costMalusAvoid, DEFAULT_BOARD_SETTINGS.costMalusAvoid),
    costCarPermanent: clampNonNegativeInt(
      r.costCarPermanent,
      DEFAULT_BOARD_SETTINGS.costCarPermanent
    ),
    costDriverPermanent: clampNonNegativeInt(
      r.costDriverPermanent,
      DEFAULT_BOARD_SETTINGS.costDriverPermanent
    ),
    costJokerSeason: clampNonNegativeInt(r.costJokerSeason, DEFAULT_BOARD_SETTINGS.costJokerSeason),
    costJokerMeeting: clampNonNegativeInt(
      r.costJokerMeeting,
      DEFAULT_BOARD_SETTINGS.costJokerMeeting
    ),
    costCarPurchase: clampNonNegativeInt(r.costCarPurchase, DEFAULT_BOARD_SETTINGS.costCarPurchase),
    bonusPointsMin,
    bonusPointsMax: Math.max(bonusPointsMin, bonusPointsMaxRaw),
    malusPointsMin,
    malusPointsMax: Math.max(malusPointsMin, malusPointsMaxRaw),
    casinoMultiplier: Math.max(
      1,
      clampNonNegativeInt(r.casinoMultiplier, DEFAULT_BOARD_SETTINGS.casinoMultiplier)
    ),
  };
}

/** Type de case par index de `Circuit.path` — généré une fois par meeting. */
export type BoardLayout = Record<number, BoardCellType>;

// --- Contrat socket serveur → client (BOARD_EVENTS.STATE / WARMUP_START) ---
// Importé en `import type` uniquement (effacé à la compilation) — pas de
// dépendance runtime malgré le cycle avec `shared/game/boardGame.ts`, qui
// importe déjà des types de ce fichier.
import type { BoardCellOutcome } from '../game/boardGame.js';

export interface PendingBoardDecision {
  playerIndex: number;
  outcome: BoardCellOutcome;
}

export interface BoardHistoryEntry {
  playerIndex: number;
  pathIndex: number;
  outcome: BoardCellOutcome;
  applied?: boolean;
}

export interface BoardStatePayload {
  gameId: string;
  layout: BoardLayout;
  order: number[];
  currentPlayerIndex: number | null;
  playerPathIndices: number[];
  /** Index sur la ligne de départ (null hors bretelle) — aligné course qual. */
  playerStartLaneIndex: (number | null)[];
  stepsRemaining: number[];
  currency: number[];
  playerIsHuman: boolean[];
  playerDone: boolean[];
  pendingDecision: PendingBoardDecision | null;
  complete: boolean;
  history: BoardHistoryEntry[];
}
